import { Box, Stack, Typography } from "@mui/material";
import { delftBlue } from "@/theme/colors";

const UserIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M6 21V19C6 17.9391 6.42143 16.9217 7.17157 16.1716C7.92172 15.4214 8.93913 15 10 15H14C15.0609 15 16.0783 15.4214 16.8284 16.1716C17.5786 16.9217 18 17.9391 18 19V21M8 7C8 8.06087 8.42143 9.07828 9.17157 9.82843C9.92172 10.5786 10.9391 11 12 11C13.0609 11 14.0783 10.5786 14.8284 9.82843C15.5786 9.07828 16 8.06087 16 7C16 5.93913 15.5786 4.92172 14.8284 4.17157C14.0783 3.42143 13.0609 3 12 3C10.9391 3 9.92172 3.42143 9.17157 4.17157C8.42143 4.92172 8 5.93913 8 7Z"
      stroke={delftBlue[500]}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const PhoneIcon = () => (
  <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M17.8472 12.6055L13.4306 10.6264L13.4184 10.6208C12.9526 10.4216 12.4177 10.471 11.9963 10.7521C11.9718 10.7682 11.9483 10.7857 11.9259 10.8046L9.64406 12.7499C8.19844 12.0477 6.70594 10.5664 6.00375 9.13957L7.95187 6.823C7.97062 6.79957 7.98844 6.77613 8.00531 6.75082C8.28032 6.33058 8.32677 5.80061 8.12906 5.33894V5.32769L6.14437 0.903628C5.88009 0.293782 5.246 -0.0694296 4.58625 0.0111279C1.95833 0.356932 -0.00475144 2.59931 0 5.24988C0 12.6936 6.05625 18.7499 13.5 18.7499C16.1506 18.7546 18.3929 16.7915 18.7388 14.1636C18.8195 13.5041 18.4567 12.8701 17.8472 12.6055ZM13.5 17.2499C6.87558 17.2426 1.50723 11.8743 1.5 5.24988C1.4927 3.35606 2.89195 1.75096 4.76906 1.49988C4.76869 1.50362 4.76869 1.50739 4.76906 1.51113L6.73781 5.91738L4.8 8.23675C4.78033 8.25938 4.76246 8.28352 4.74656 8.30894C4.45961 8.74926 4.42405 9.30765 4.65281 9.78082C5.50219 11.518 7.2525 13.2552 9.00844 14.1036C9.48515 14.3303 10.0459 14.2897 10.485 13.9968C10.5091 13.9805 10.5322 13.963 10.5544 13.9443L12.8334 11.9999L17.2397 13.9733C17.2397 13.9733 17.2472 13.9733 17.25 13.9733C17.002 15.8532 15.3962 17.2563 13.5 17.2499Z"
      fill={delftBlue[500]}
    />
  </svg>
);

const MailIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M18.25 4.25H5.75C4.09315 4.25 2.75 5.59315 2.75 7.25V16.75C2.75 18.4069 4.09315 19.75 5.75 19.75H18.25C19.9069 19.75 21.25 18.4069 21.25 16.75V7.25C21.25 5.59315 19.9069 4.25 18.25 4.25Z"
      stroke={delftBlue[500]}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M2.75 8L11.165 11.866C11.4269 11.9863 11.7118 12.0486 12 12.0486C12.2882 12.0486 12.5731 11.9863 12.835 11.866L21.25 8"
      stroke={delftBlue[500]}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CompanyHoverInfo = ({ company }) => {
  const { name, location, adminId } = company || {};

  // Derive admin details (if a company admin is present)
  const adminName =
    adminId?.fullName || adminId?.username || adminId?.email || "";
  const adminEmail = adminId?.email || "";
  const adminPhone = adminId?.phoneNumber || "";
  const hasAdmin = Boolean(adminId && adminName);

  return (
    <Box
      sx={{
        padding: 1,
        background: "white",
        boxShadow: "0px 2px 4px -2px rgba(0, 0, 0, 0.10)",
        overflow: "hidden",
        borderRadius: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        minWidth: 300,
        maxWidth: 417,
      }}
    >
      {/* Company Name and Location */}
      <Stack
        sx={{
          alignSelf: "stretch",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <Box
          sx={{
            alignSelf: "stretch",
            padding: 0.5,
            borderRadius: 0.5,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              flex: "1 1 0",
              color: delftBlue[500],
              fontSize: 16,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 600,
              wordWrap: "break-word",
            }}
          >
            {name || "Company Name"}
          </Typography>
        </Box>
        <Box
          sx={{
            alignSelf: "stretch",
            padding: 0.5,
            borderRadius: 0.5,
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              color: delftBlue[500],
              fontSize: 13,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              wordWrap: "break-word",
            }}
          >
            {location || "Location"}
          </Typography>
        </Box>
      </Stack>

      {/* Divider */}
      <Box
        sx={{
          width: "100%",
          height: 1,
          background: "#CCCCCC",
        }}
      />

      {/* Company Admin Details (shown only if an admin exists) */}
      {hasAdmin && (
        <Stack
          sx={{
            alignSelf: "stretch",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {/* Admin Name */}
          <Box
            sx={{
              alignSelf: "stretch",
              padding: 0.5,
              borderRadius: 0.5,
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <UserIcon />
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: 16,
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 700,
                wordWrap: "break-word",
              }}
            >
              {adminName}
            </Typography>
          </Box>

          {/* Admin Phone and Email */}
          <Stack
            direction="row"
            sx={{
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            {adminPhone && (
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 0.5,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <PhoneIcon />
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: 16,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    wordWrap: "break-word",
                  }}
                >
                  {adminPhone}
                </Typography>
              </Box>
            )}
            {adminEmail && (
              <Box
                sx={{
                  padding: 0.5,
                  borderRadius: 0.5,
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <MailIcon />
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: 16,
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 500,
                    wordWrap: "break-word",
                  }}
                >
                  {adminEmail}
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default CompanyHoverInfo;
